import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Star, X } from "lucide-react";
import { api } from "@/lib/api";

export const InstrumentPicker = ({ symbols = [], value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [marks, setMarks] = useState([]);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    api
      .get("/me/bookmarks")
      .then(({ data }) => setMarks(data.bookmarks || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const away = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const list = useMemo(() => {
    const q = query.trim().toUpperCase();
    const hits = symbols.filter((s) => s.toUpperCase().includes(q));
    const starred = hits.filter((s) => marks.includes(s));
    const rest = hits.filter((s) => !marks.includes(s));
    return [...starred, ...rest];
  }, [symbols, marks, query]);

  useEffect(() => setCursor(0), [query, open]);

  const toggleMark = async (symbol, e) => {
    e.stopPropagation();
    try {
      const { data } = marks.includes(symbol)
        ? await api.delete(`/me/bookmarks/${encodeURIComponent(symbol)}`)
        : await api.post("/me/bookmarks", { symbol });
      setMarks(data.bookmarks || []);
    } catch {}
  };

  const pick = (symbol) => {
    onChange(symbol);
    setOpen(false);
    setQuery("");
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, list.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter" && list[cursor]) {
      e.preventDefault();
      pick(list[cursor]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={boxRef} data-testid="instrument-picker">
      {marks.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="bookmark-chips">
          {marks.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`bookmark-chip-${s}`}
              onClick={() => pick(s)}
              className={`group inline-flex items-center gap-1 rounded-sm border px-2 py-1 font-mono text-[11px] transition-colors duration-200 ${
                s === value
                  ? "border-emerald-600 bg-emerald-600/15 text-emerald-300"
                  : "border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100"
              }`}
            >
              {s}
              <X
                className="h-3 w-3 opacity-0 group-hover:opacity-70 hover:!opacity-100"
                onClick={(e) => toggleMark(s, e)}
                data-testid={`bookmark-remove-${s}`}
              />
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        data-testid="instrument-trigger"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-2 font-mono text-sm text-zinc-100 hover:border-zinc-600 focus:border-emerald-500 focus:outline-none transition-colors duration-200"
      >
        <span>{value || "Select instrument"}</span>
        <Search className="h-3.5 w-3.5 text-zinc-400" />
      </button>

      {open && (
        <div
          data-testid="instrument-dropdown"
          className="absolute z-30 mt-1 w-full border border-zinc-700 bg-zinc-950/95 backdrop-blur-sm shadow-xl"
        >
          <div className="p-2 border-b border-zinc-800">
            <input
              autoFocus
              data-testid="instrument-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKey}
              placeholder="Search instruments…"
              className="w-full rounded-sm bg-zinc-900 border border-zinc-800 px-2.5 py-1.5 font-mono text-xs text-zinc-100 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {list.length === 0 && (
              <li className="px-3 py-2 text-xs text-zinc-400 font-mono" data-testid="instrument-empty">
                No instrument matches “{query}”
              </li>
            )}
            {list.map((s, i) => (
              <li key={s}>
                <div
                  role="button"
                  tabIndex={0}
                  data-testid={`instrument-option-${s}`}
                  onClick={() => pick(s)}
                  onMouseEnter={() => setCursor(i)}
                  className={`flex items-center justify-between px-3 py-1.5 font-mono text-xs cursor-pointer transition-colors duration-150 ${
                    i === cursor ? "bg-emerald-600/15 text-emerald-200" : "text-zinc-300"
                  }`}
                >
                  <span className={s === value ? "text-emerald-300" : ""}>{s}</span>
                  <Star
                    data-testid={`bookmark-toggle-${s}`}
                    onClick={(e) => toggleMark(s, e)}
                    className={`h-3.5 w-3.5 transition-colors duration-200 ${
                      marks.includes(s)
                        ? "text-amber-400 fill-amber-400"
                        : "text-zinc-400 hover:text-amber-300"
                    }`}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
